import React from 'react'
import { useT } from '../i18n'
import { getProductionSpeciesDescription, getProductionSpeciesLabel } from '../utils/productionSpeciesLabels'
import {
  PRODUCTION_SPECIES_OPTIONS,
  PRODUCTION_SPECIES_PRESETS,
  normalizeSpeciesKey,
  type ProductionSpeciesKey
} from '../data/productionSpeciesPresets'

export type SelectorOption = {
  value: string
  label: string
  key?: string
  disabled?: boolean
  /** Heading of the <optgroup> this option belongs to; consecutive options with the same heading share one group. */
  group?: string
}

export type BaseSelectorProps = {
  label: string
  value: string
  options: SelectorOption[]
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  minWidth?: number
  // Optional id to attach to the <select> (and htmlFor on the <label>). Kept
  // opt-in so we don't force IDs onto every consumer; the Lots screen sets
  // these to preserve the `#lots-filter-farm` / `#lots-filter-production-unit`
  // locators that existing lot-story + lot-transfer E2E specs rely on.
  id?: string
}

export type SpeciesControlProps = {
  value: ProductionSpeciesKey
  onChange: (value: ProductionSpeciesKey) => void
  disabled?: boolean
  label?: string
  helperText?: string
  showHelper?: boolean
  minWidth?: number
}

export type ContextSelectorBarProps = {
  species?: SpeciesControlProps
  farm: BaseSelectorProps
  /** Omitted where the screen is facility-wide (Inventario, findings #144). */
  productionUnit?: BaseSelectorProps
  lot?: BaseSelectorProps
  summary?: React.ReactNode
  style?: React.CSSProperties
}

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '6px',
  fontWeight: 600,
  color: '#475569'
}

const buildSelectStyle = (minWidth: number, disabled: boolean): React.CSSProperties => ({
  padding: '10px 12px',
  borderRadius: '10px',
  border: '1px solid #cbd5f5',
  backgroundColor: disabled ? '#f1f5f9' : '#ffffff',
  minWidth: `${minWidth}px`,
  cursor: disabled ? 'not-allowed' : 'pointer'
})

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px',
  alignItems: 'flex-end'
}

const renderOption = (option: SelectorOption) => (
  <option key={option.key ?? option.value} value={option.value} disabled={option.disabled}>
    {option.label}
  </option>
)

const groupConsecutiveOptions = (options: SelectorOption[]): Array<{ heading?: string; options: SelectorOption[] }> => {
  const entries: Array<{ heading?: string; options: SelectorOption[] }> = []
  for (const option of options) {
    const last = entries[entries.length - 1]
    if (option.group !== undefined && last && last.heading === option.group) last.options.push(option)
    else entries.push({ heading: option.group, options: [option] })
  }
  return entries
}

const SelectField = ({
  label,
  value,
  options,
  onChange,
  placeholder,
  disabled,
  minWidth,
  id
}: BaseSelectorProps) => (
  <div>
    <label style={fieldLabelStyle} htmlFor={id}>{label}</label>
    <select
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      style={buildSelectStyle(minWidth ?? 220, Boolean(disabled))}
    >
      <option value="">{placeholder ?? 'Select...'}</option>
      {groupConsecutiveOptions(options).map((entry) => (
        entry.heading === undefined
          ? renderOption(entry.options[0])
          : (
            <optgroup key={`group:${entry.options[0].key ?? entry.options[0].value}`} label={entry.heading}>
              {entry.options.map(renderOption)}
            </optgroup>
          )
      ))}
    </select>
  </div>
)

export default function ContextSelectorBar({
  species,
  farm,
  productionUnit,
  lot,
  summary,
  style
}: ContextSelectorBarProps) {
  const t = useT()
  const speciesMinWidth = species?.minWidth ?? 220
  const resolvedSpeciesHelper = species
    ? (species.helperText ?? getProductionSpeciesDescription(t, species.value))
    : ''

  return (
    <div style={style ? { ...containerStyle, ...style } : containerStyle}>
      {species ? (
        <div style={{ minWidth: speciesMinWidth }}>
          <label style={fieldLabelStyle}>{species.label ?? t('sfrManagement.speciesPreset.label')}</label>
          <select
            value={species.value}
            onChange={(event) => species.onChange(normalizeSpeciesKey(event.target.value))}
            disabled={species.disabled}
            style={buildSelectStyle(speciesMinWidth, Boolean(species.disabled))}
          >
            {PRODUCTION_SPECIES_OPTIONS.map((option) => (
              <option key={option.key} value={option.key} title={getProductionSpeciesDescription(t, option.key)}>
                {getProductionSpeciesLabel(t, option.key)}
              </option>
            ))}
          </select>
          {species.showHelper && resolvedSpeciesHelper ? (
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px', maxWidth: speciesMinWidth }}>
              {resolvedSpeciesHelper}
            </div>
          ) : null}
        </div>
      ) : null}

      <SelectField {...farm} />
      {productionUnit ? <SelectField {...productionUnit} /> : null}
      {lot ? <SelectField {...lot} /> : null}

      {summary ? summary : null}
    </div>
  )
}
